import { normalizeDrawings, type Drawing } from '../drawings/model';
import { normalizeInstances, type IndicatorInstance } from '../indicators/instances';
import { isChartType, isTimeframe, type ChartType, type Timeframe } from './chartModel';
import { normalizeProperties, type ChartProperties } from './chartProperties';

/** What a named chart template remembers: how the chart looks, its indicators and, if asked, its drawings. */
export interface ChartTemplate {
  type: ChartType;
  tf: Timeframe | null;
  bidAsk: boolean;
  indicators: IndicatorInstance[];
  /** Grid, volumes, scale and colours (F8); absent in templates saved before they existed. */
  properties?: ChartProperties;
  /** Drawings are tied to prices and times, so they are part of a template only when the user chose to include them. */
  drawings: Drawing[] | null;
}

export type ChartTemplates = Record<string, ChartTemplate>;

export const MAX_TEMPLATES = 20;
export const MAX_TEMPLATE_NAME = 40;
/** The synced preferences are limited to 48 KB; leave room for the layout and watch list. */
export const PREFS_LIMIT_BYTES = 44 * 1024;

export const cleanName = (name: string) => name.replace(/\s+/g, ' ').trim().slice(0, MAX_TEMPLATE_NAME);

/** A message when a template cannot be saved under this name, otherwise null. */
export function nameProblem(name: string, existing: ChartTemplates): string | null {
  const clean = cleanName(name);
  if (clean === '') return 'Give the template a name.';
  if (!(clean in existing) && Object.keys(existing).length >= MAX_TEMPLATES) {
    return `You can keep up to ${MAX_TEMPLATES} templates. Delete one first.`;
  }
  return null;
}

export function normalizeTemplates(raw: unknown): ChartTemplates {
  const out: ChartTemplates = {};
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return out;
  for (const [name, value] of Object.entries(raw as Record<string, unknown>).slice(0, MAX_TEMPLATES)) {
    const key = cleanName(name);
    if (!key || typeof value !== 'object' || value === null) continue;
    const t = value as Partial<ChartTemplate>;
    out[key] = {
      type: isChartType(t.type) ? t.type : 'candles',
      tf: isTimeframe(t.tf) ? t.tf : null,
      bidAsk: t.bidAsk !== false,
      indicators: normalizeInstances(t.indicators),
      ...(t.properties ? { properties: normalizeProperties(t.properties) } : {}),
      drawings: Array.isArray(t.drawings) ? normalizeDrawings(t.drawings) : null,
    };
  }
  return out;
}

export function makeTemplate(
  chart: {
    type: ChartType;
    tf: Timeframe;
    bidAsk: boolean;
    indicators: IndicatorInstance[];
    properties?: ChartProperties;
  },
  drawings: Drawing[] | null,
  includeTimeframe: boolean,
): ChartTemplate {
  return {
    type: chart.type,
    tf: includeTimeframe ? chart.tf : null,
    bidAsk: chart.bidAsk,
    indicators: chart.indicators,
    ...(chart.properties ? { properties: chart.properties } : {}),
    drawings,
  };
}

/** What applying a template changes on a chart. The symbol and the one-click setting are never part of it. */
export function applyTemplate(t: ChartTemplate): {
  patch: {
    type: ChartType;
    bidAsk: boolean;
    indicators: IndicatorInstance[];
    tf?: Timeframe;
    properties?: ChartProperties;
  };
  drawings: Drawing[] | null;
} {
  return {
    patch: {
      type: t.type,
      bidAsk: t.bidAsk,
      // a fresh id for each indicator, so applying one template to two charts never shares an id between them
      indicators: t.indicators.map((i) => ({
        ...i,
        uid: `${i.uid}-${Math.floor(Math.random() * 1e6).toString(36)}`,
      })),
      ...(t.tf ? { tf: t.tf } : {}),
      ...(t.properties ? { properties: t.properties } : {}),
    },
    drawings: t.drawings,
  };
}

/** The size the preferences would have with these values, in bytes. */
export const sizeOf = (value: unknown): number => new TextEncoder().encode(JSON.stringify(value)).length;
